import React, {createContext, PropsWithChildren, useCallback, useContext, useMemo, useState} from "react";
import {SharedElevationDialog} from "../elevationTool/components/SharedElevationDialog";

type Resolution = "update-all" | "detach";

type ConfirmState = {
    openings: string[];
    resolve(resolution: Resolution | null): void;
}

const ConfirmSharedIdentityContext = createContext({
    // resolves to the chosen resolution, or null if the user cancelled
    confirm: async (openings: string[]): Promise<Resolution | null> => null,
});

export function useConfirmSharedIdentity() {
    return useContext(ConfirmSharedIdentityContext);
}

// Prompts when a grid edit changes a Synced identity column on an opening that shares its
// screen elevation with others (see drw shared-identity gate). Reuses the elevation tool's
// SharedElevationDialog so the wording matches.
export function ConfirmSharedIdentityProvider(props: PropsWithChildren<{}>) {
    const [state, setState] = useState<ConfirmState | null>(null);

    const confirm = useCallback((openings: string[]) => {
        return new Promise<Resolution | null>((resolve) => {
            setState({openings, resolve});
        });
    }, []);

    const ctx = useMemo(() => ({confirm}), [confirm]);

    const done = (resolution: Resolution | null) => {
        state?.resolve(resolution);
        setState(null);
    };

    return (
        <ConfirmSharedIdentityContext.Provider value={ctx}>
            {props.children}
            {state && (
                <SharedElevationDialog
                    conflict={{openings: state.openings}}
                    onCancel={() => done(null)}
                    onResolve={done}
                />
            )}
        </ConfirmSharedIdentityContext.Provider>
    );
}
